import { generateSeedSet } from '../../../shared/render-seeds.js';

export function isPartnerModel(model) {
  return model?.parameters?.isExternalAPI === true && model?.parameters?.premiumOnly === true;
}

export function modelRequiresPremiumSpark(model) {
  return isPartnerModel(model) || model?.parameters?.premiumOnly === true || model?.tags?.includes('premium');
}

export function hasPlanCoverage(subscription) {
  return subscription?.active === true && ['unlimited', 'unlimited_pro'].includes(subscription.tier);
}

export function modelIsCovered(model, subscription) {
  return hasPlanCoverage(subscription) && !isPartnerModel(model);
}

export function entitlementFingerprint(subscription) {
  const fairUse = subscription?.fairUse;
  return JSON.stringify({
    active: subscription?.active === true,
    tier: subscription?.tier || null,
    status: subscription?.status || null,
    discount: subscription?.frontierDiscountBps || 0,
    // Account balance updates also change the running usage counters. They
    // do not change the reviewed billing entitlement or pause Relaxed work.
    fairUse: fairUse ? {
      limited: fairUse.limited === true,
      fastRenderingPaused: fairUse.fastRenderingPaused === true,
      relaxedUnrestricted: fairUse.relaxedUnrestricted === true,
      resetAt: fairUse.resetAt || null
    } : null
  });
}

function freezeRecord(value) {
  if (Array.isArray(value) || (value && Object.getPrototypeOf(value) === Object.prototype)) {
    Object.values(value).forEach(freezeRecord);
    Object.freeze(value);
  }
  return value;
}

function detachRecord(value) {
  if (Array.isArray(value)) return value.map(detachRecord);
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, detachRecord(item)]));
  }
  return value;
}

export function freezeRequest(value) {
  return freezeRecord(detachRecord(value));
}

// One seed set is shared across every prompt/model batch. Build once, then
// quote and submit these same slots; confirmation never rolls another seed.
export async function createImageRequest({
  prompts,
  modelIds,
  imagesPerPrompt,
  accountId,
  subscription,
  getModel,
  resolveModelParams,
  sourceFingerprint,
  billingMode = 'auto',
  id = crypto.randomUUID(),
  seedSet = generateSeedSet(imagesPerPrompt)
}) {
  const slots = [];
  for (const [modelIndex, modelId] of modelIds.entries()) {
    const model = getModel(modelId);
    if (!model) throw new Error(`Model ${modelId} is missing from the current catalog.`);
    const params = await resolveModelParams(modelId);
    const covered = billingMode !== 'tokens' && modelIsCovered(model, subscription);
    const payment = covered ? 'subscription' : 'tokens';
    for (const [promptIndex, prompt] of prompts.entries()) {
      const batchIndex = modelIndex * prompts.length + promptIndex;
      for (let imageIndex = 0; imageIndex < imagesPerPrompt; imageIndex += 1) {
        const text = typeof prompt === 'string' ? prompt : JSON.stringify(prompt);
        slots.push({
          id: `${id}:${batchIndex}:${imageIndex}`,
          batchIndex,
          imageIndex,
          prompt: text,
          modelId,
          modelName: model.name,
          premium: modelRequiresPremiumSpark(model),
          partner: isPartnerModel(model),
          projectConfig: {
            ...params,
            type: 'image',
            modelId,
            positivePrompt: text,
            negativePrompt: params.negativePrompt || '',
            stylePrompt: '',
            numberOfImages: 1,
            numberOfPreviews: params.numberOfPreviews ?? (isPartnerModel(model) ? 0 : 4),
            seed: seedSet[imageIndex],
            tokenType: 'spark',
            // Recovery can associate a pre-ACK project with its exact saved
            // slot. The SDK supplies a fresh operationId for each project.
            attribution: {
              workloadKind: 'direct', agentSurface: 'native_web', executionMode: 'browser',
              operationScope: 'child', rootOperationId: id, parentOperationId: `${id}:${batchIndex}:${imageIndex}`
            },
            billingMode: payment
          }
        });
      }
    }
  }
  return freezeRecord(detachRecord({
    id,
    accountId,
    network: slots[0]?.projectConfig.network || 'fast',
    sourceFingerprint,
    entitlementFingerprint: entitlementFingerprint(subscription),
    modelIds: [...modelIds],
    imagesPerPrompt,
    totalBatches: prompts.length * modelIds.length,
    createdAt: Date.now(),
    slots
  }));
}

// An explicit retry reviews the original slots against the account and UI
// context at the start of this attempt. It never inherits an old approval.
export function prepareExistingRequest(request, { accountId, sourceFingerprint, subscription }) {
  if (request.accountId !== accountId) {
    const error = new Error('This request belongs to another account.');
    error.code = 'SESSION_IDENTITY_CHANGED';
    throw error;
  }
  return freezeRecord(detachRecord({ ...request, sourceFingerprint,
    entitlementFingerprint: entitlementFingerprint(subscription) }));
}

export function toImageEstimate(params, imageCount = 1) {
  return {
    model: params.modelId,
    network: params.network || 'fast',
    tokenType: params.tokenType,
    billingMode: params.billingMode,
    imageCount,
    stepCount: params.steps ?? 1,
    previewCount: params.numberOfPreviews ?? 0,
    width: params.width,
    height: params.height,
    guidance: params.guidance,
    sampler: params.sampler,
    cnEnabled: !!params.controlNet,
    startingImageStrength: params.startingImageStrength,
    contextImages: params.contextImages?.length || 0,
    gptImageQuality: params.gptImageQuality,
    outputFormat: params.outputFormat
  };
}

// Exact decimal addition avoids rounding down fractional Spark in mixed batches.
const DECIMAL_SCALE = 10n ** 18n;
export function decimalUnits(value) {
  const match = /^(\d+)(?:\.(\d*))?(?:e([+-]?\d+))?$/i.exec(String(value).trim());
  if (!match) throw new Error('The server returned an invalid cost or balance.');
  const digits = BigInt(match[1] + (match[2] || ''));
  const shift = 18 + Number(match[3] || 0) - (match[2]?.length || 0);
  if (shift >= 0) return digits * 10n ** BigInt(shift);
  const divisor = 10n ** BigInt(-shift);
  return digits / divisor + (digits % divisor > 0n ? 1n : 0n);
}

export function unitsToDecimal(units) {
  const whole = units / DECIMAL_SCALE;
  const fraction = String(units % DECIMAL_SCALE).padStart(18, '0').replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : String(whole);
}

export async function quoteImageRequest(request, estimateCost) {
  const groups = new Map();
  for (const slot of request.slots) {
    const estimate = { ...toImageEstimate(slot.projectConfig), partner: slot.partner,
      prompt: slot.partner && slot.projectConfig.contextImages?.length ? slot.projectConfig.positivePrompt : undefined };
    const key = JSON.stringify(estimate);
    if (!groups.has(key)) groups.set(key, { estimate, slots: [] });
    groups.get(key).slots.push(slot);
  }

  let total = 0n;
  let premiumTotal = 0n;
  const lines = [];
  for (const { estimate, slots } of groups.values()) {
    // Each slot is submitted as its own one-image project. A multi-image quote
    // can discount cached reference inputs within a project (GPT Image 2),
    // which does not match these separate submissions.
    const cost = await estimateCost({ ...estimate, imageCount: 1 });
    const covered = estimate.billingMode === 'subscription';
    const amount = decimalUnits(cost.spark) * BigInt(slots.length);
    if (!covered) {
      total += amount;
      if (slots[0].premium) premiumTotal += amount;
    }
    lines.push({
      modelId: slots[0].modelId,
      modelName: slots[0].modelName,
      count: slots.length,
      covered,
      premium: slots[0].premium,
      partner: slots[0].partner,
      discountBpsApplied: cost.discountBpsApplied || 0,
      amount: covered ? '0' : unitsToDecimal(amount),
      retailAmount: unitsToDecimal(amount),
      settings: estimate,
      estimatedRenderSeconds: cost.estimatedRenderSeconds,
      estimatedTotalSeconds: cost.estimatedTotalSeconds,
      dailyFairUsePct: cost.dailyFairUsePct
    });
  }
  return freezeRecord({
    requestId: request.id,
    accountId: request.accountId,
    slotIds: request.slots.map(slot => slot.id),
    kind: 'images',
    lines,
    total: unitsToDecimal(total),
    premiumTotal: unitsToDecimal(premiumTotal),
    currency: 'Spark',
    imageCount: request.slots.length
  });
}

export function assertRequestOwner(request, accountId) {
  if (!request.accountId || request.accountId !== accountId) {
    const error = new Error('Your account changed. Review this generation in its original account.');
    error.code = 'REQUEST_CHANGED';
    error.request = request;
    throw error;
  }
}

export function assertRequestCurrent(request, { accountId, sourceFingerprint, subscription }) {
  if (request.accountId !== accountId || request.sourceFingerprint !== sourceFingerprint ||
      request.entitlementFingerprint !== entitlementFingerprint(subscription)) {
    const error = new Error('Your account, plan, or settings changed. Review this generation again.');
    error.code = 'REQUEST_CHANGED';
    error.request = request;
    throw error;
  }
}

export function assertQuoteAffordable(quote, { spark = 0, premiumSpark = 0 }) {
  const total = decimalUnits(quote.total);
  const premium = decimalUnits(quote.premiumTotal);
  const availableSpark = decimalUnits(Number(spark) < 0 ? 0 : spark);
  const availablePremium = decimalUnits(Number(premiumSpark) < 0 ? 0 : premiumSpark);
  // Premium credit grants model access; charges draw on the total net Spark
  // balance. The credit multiplier is not a separate amount to fund the quote.
  const needsPremium = premium > 0n && availablePremium <= 0n;
  if (needsPremium || total > availableSpark) {
    const error = new Error(needsPremium
      ? 'This selection requires Premium Spark access. Purchased Spark unlocks these models.'
      : 'You do not have enough Spark for this generation.');
    error.code = needsPremium ? 4054 : 'INSUFFICIENT_CREDITS';
    error.kind = needsPremium ? 'spark-tier' : 'balance';
    error.currency = 'Spark';
    error.required = quote.total;
    error.available = String(spark);
    error.quote = quote;
    throw error;
  }
}
